import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  ArrowRight,
  Play,
  ChevronDown,
  ChevronRight,
  Check,
  Clapperboard,
  Pizza,
  Heart,
  Upload,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PLANS } from '@/data/plans'
import { contentService } from '@/services/contentService'
import { guildAuthUrl } from '@/lib/guildUrl'

import kinoraLogo from '@shared-assets/kinora_logo.svg'

/* ─────────────────────────────────────────────
   DATA
   ───────────────────────────────────────────── */

const STATS = [
  { value: '100+', label: 'Indie Titles' },
  { value: '50+', label: 'Creators' },
  { value: '$1 = 1 slice', label: 'Sent Directly to Creators' },
  { value: '100%', label: 'To Creators — 0% Platform Cut' },
]

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: <Play className="w-6 h-6" />,
    title: 'Subscribe & Watch',
    description:
      'Pick a plan starting at $8/mo. Stream indie films, series, documentaries, and more — unlimited.',
  },
  {
    step: '02',
    icon: <Pizza className="w-6 h-6" />,
    title: 'Get Your Slices',
    description:
      'Every month you receive slices — digital tokens included with your plan. The bigger your plan, the more slices you get.',
  },
  {
    step: '03',
    icon: <Heart className="w-6 h-6" />,
    title: 'Support What You Love',
    description:
      'Donate slices to the creators and projects you believe in. Every slice = $1 sent directly to the creator. No middlemen.',
  },
]

const GENRES = [
  'Sci-Fi',
  'Drama',
  'Horror',
  'Comedy',
  'Thriller',
  'Documentary',
  'Animation',
  'Romance',
  'Action',
  'Fantasy',
]

const FAQ_ITEMS = [
  {
    q: 'What can I watch on Kinora?',
    a: 'Kinora features a curated library of indie films, series, documentaries, talk shows, and more — all from independent creators around the world. New titles are added regularly.',
  },
  {
    q: 'What are slices and how do they work?',
    a: 'Slices are digital tokens included with your subscription. Each slice is worth $1. You can donate them to any creator or project on the platform. 100% of your slice donations go directly to the creators — Kinora takes nothing from donations.',
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. There are no contracts or commitments. Cancel anytime from your account settings and you'll keep access until the end of your billing period. Unused slices do not carry over.",
  },
  {
    q: 'How is Kinora different from other streaming services?',
    a: "Most streaming platforms pay creators through opaque algorithms. On Kinora, you decide who gets funded. Your subscription includes slices you can donate directly to creators and crowdfunding campaigns. It's streaming with a purpose.",
  },
  {
    q: "I'm a filmmaker — how do I get my content on Kinora?",
    a: 'Kinora is open to independent creators. Click "Start Publishing" in the footer to begin the publisher onboarding process. You\'ll set up your studio page, connect Stripe for payouts, and start uploading your titles.',
  },
  {
    q: 'What is the Kinora Guild?',
    a: 'The Guild is Kinora\'s creator network — where filmmakers, editors, actors, composers, and every other craft find each other. Build a skill profile, apply to open roles on real productions, and earn verified credits that appear on Kinora watch pages. Visit the Guild page from the footer to learn more — your Kinora account works there too.',
  },
  {
    q: 'What devices can I watch on?',
    a: 'Kinora works in any modern web browser on desktop, tablet, and mobile. Native apps are on the roadmap.',
  },
]

/* ─────────────────────────────────────────────
   COMPONENTS
   ───────────────────────────────────────────── */

/** Staggered fade-in on scroll */
const FadeIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.6s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}

/** FAQ accordion item */
const FAQItem = ({ item, open, toggle }) => (
  <button
    onClick={toggle}
    className="w-full text-left group"
    aria-expanded={open}
  >
    <div className="flex items-center justify-between py-5 border-b border-border/40">
      <span className="text-base md:text-lg font-medium pr-4 group-hover:text-primary transition-colors">
        {item.q}
      </span>
      <ChevronDown
        className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-300 ${
          open ? 'rotate-180 text-primary' : ''
        }`}
      />
    </div>
    <div
      className="overflow-hidden transition-all duration-300"
      style={{ maxHeight: open ? '200px' : '0px', opacity: open ? 1 : 0 }}
    >
      <p className="text-muted-foreground text-sm md:text-base pb-5 pt-3 leading-relaxed">
        {item.a}
      </p>
    </div>
  </button>
)

/** Floating genre pill ticker — repeats enough copies to cover ultrawide viewports.
    Animation translates -50% of the full track; each half holds REPEATS×GENRES so the
    second half is always wider than any reasonable viewport, preventing visible gaps. */
const GenreTicker = () => {
  const REPEATS = 4
  const halfList = Array.from({ length: REPEATS }, () => GENRES).flat()
  const doubled = [...halfList, ...halfList]
  return (
    <div className="relative overflow-hidden py-5 mask-fade-x">
      <div className="flex gap-3 pr-3 animate-[ticker_50s_linear_infinite] w-max">
        {doubled.map((g, i) => (
          <span
            key={`${g}-${i}`}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-border/50 bg-card/40 text-sm text-muted-foreground whitespace-nowrap backdrop-blur-sm"
          >
            {g}
          </span>
        ))}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────
   MAIN PAGE
   ───────────────────────────────────────────── */

const Landing = () => {
  const navigate = useNavigate()
  const [openFAQ, setOpenFAQ] = useState(null)
  const [showcase, setShowcase] = useState([])

  // Real catalogue titles for the showcase — hidden entirely if none load.
  useEffect(() => {
    let cancelled = false
    contentService
      .getRecentContent({ limit: 5 })
      .then((works) => {
        if (!cancelled) setShowcase((works || []).filter((w) => w.poster_url || w.thumbnail))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="bg-background text-foreground min-h-screen overflow-x-hidden">
      {/* ── Inline keyframes & utilities ── */}
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: .35; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .mask-fade-x {
          mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
        }
        .text-gradient {
          background: linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / .7) 50%, hsl(var(--accent, var(--primary))) 100%);
          background-size: 200% 200%;
          animation: gradient-shift 6s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glow-card:hover {
          box-shadow: 0 0 40px -12px hsl(var(--primary) / .25);
        }
      `}</style>

      {/* ════════════════════════════════════════
          HERO
         ════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        {/* Ambient blurs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-28">
          <FadeIn className="flex flex-col items-center text-center gap-6">
            <div className="flex flex-col items-center gap-3 md:gap-4 max-w-4xl">
              <div className="text-4xl sm:text-5xl md:text-7xl leading-none">
                <img
                  src={kinoraLogo}
                  alt="Kinora"
                  className="h-[1.25em] w-auto drop-shadow-lg select-none"
                  draggable={false}
                />
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] text-balance">
                Stream Indie.{' '}
                <span className="text-gradient whitespace-nowrap">Fund Creators.</span>
              </h1>
            </div>

            <p className="text-base md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Watch films, series, and documentaries from independent creators
              worldwide — and use your <strong className="text-foreground">Slices</strong>{' '}
              to directly fund the projects you believe in.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <Button
                size="lg"
                className="group text-base px-8 h-12"
                onClick={() => navigate('/browse')}
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                Start Watching
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="group text-base px-8 h-12"
                onClick={() => navigate('/pricing')}
              >
                See Plans
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-1">
              Cancel anytime &middot; Plans from $8/mo &middot; 100% of slices go to creators
            </p>
          </FadeIn>
        </div>

        {/* Genre ticker */}
        <FadeIn delay={300}>
          <GenreTicker />
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════
          STATS BAR
         ════════════════════════════════════════ */}
      <section className="border-y border-border/30 bg-card/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-6 md:py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 80} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-gradient">
                  {s.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          HOW IT WORKS — The Slice Mechanic (slices = $1 creator tokens)
         ════════════════════════════════════════ */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <FadeIn className="text-center mb-10">

            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
              How Slices Work
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Your subscription does more than unlock content — it gives you the
              power to directly fund the creators and projects you care about.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <FadeIn key={step.step} delay={i * 150}>
                <div className="relative group">
                  {i < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-40px)] border-t border-dashed border-border/40" />
                  )}
                  <Card className="bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all duration-300 glow-card">
                    <CardContent className="p-6 md:p-8 text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                        {step.icon}
                      </div>
                      <div className="text-xs font-bold text-primary/60 uppercase tracking-widest mb-2">
                        Step {step.step}
                      </div>
                      <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          CONTENT SHOWCASE — real catalogue titles
         ════════════════════════════════════════ */}
      {showcase.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <FadeIn>
            <div className="flex items-end justify-between mb-8">
              <div>

                <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
                  Discover What's On
                </h2>
              </div>
              <Button
                variant="ghost"
                className="hidden sm:flex text-muted-foreground hover:text-primary"
                onClick={() => navigate('/browse')}
              >
                Browse All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </FadeIn>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {showcase.map((item, i) => (
              <FadeIn key={item.id} delay={i * 80}>
                <div
                  className="group relative aspect-[2/3] rounded-lg overflow-hidden cursor-pointer border border-border/30 glow-card transition-all duration-300 hover:-translate-y-1"
                  onClick={() => navigate(item.slug ? `/watch/${item.slug}` : '/browse')}
                >
                  <img
                    src={item.poster_url || item.thumbnail}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col justify-end p-3 md:p-4 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    {item.tags?.[0] && (
                      <span className="text-[10px] uppercase tracking-widest text-primary/80 font-medium">
                        {item.tags[0]}
                      </span>
                    )}
                    <h3 className="text-sm md:text-base font-bold text-white leading-tight mt-0.5">
                      {item.title}
                    </h3>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="relative">
                      <div
                        className="absolute inset-0 rounded-full bg-primary/30"
                        style={{ animation: 'pulse-ring 1.5s ease-out infinite' }}
                      />
                      <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-primary-foreground ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════
          PRICING
         ════════════════════════════════════════ */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <FadeIn className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
              Simple Plans. Real Impact.
            </h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
              Every plan includes unlimited streaming. The difference is how many slices
              you get to support creators each month.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
            {PLANS.map((plan, i) => (
              <FadeIn key={plan.tier} delay={i * 120}>
                <Card
                  className={`relative h-full border glow-card transition-all duration-300 hover:-translate-y-1 ${
                    plan.featured
                      ? 'border-primary/50 bg-card/80'
                      : 'border-border/40 bg-card/50'
                  }`}
                >
                  {plan.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground px-3 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-6 md:p-8 flex flex-col h-full">
                    <h3 className="text-lg font-bold">{plan.tier}</h3>
                    <div className="mt-3 mb-1">
                      <span className="text-4xl font-extrabold">${plan.monthlyPrice}</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    <p className="text-sm text-primary font-medium mb-1">
                      {plan.slices} slices/mo
                    </p>
                    <p className="text-xs text-muted-foreground mb-4">
                      or ${plan.annualPrice}/yr — 2 months free
                    </p>

                    <ul className="space-y-2.5 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="mt-6 w-full h-11 group"
                      variant={plan.featured ? 'default' : 'outline'}
                      onClick={() => navigate('/pricing')}
                    >
                      Start with {plan.tier}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={400}>
            <p className="text-center text-xs text-muted-foreground mt-6">
              Billed monthly or annually &middot; Cancel anytime &middot; Access continues until end of billing period
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ════════════════════════════════════════
          THE ECOSYSTEM — one account, three ways in
         ════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <FadeIn className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            One account.{' '}
            <span className="text-gradient">The whole ecosystem.</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Kinora is more than a streaming service — it's where indie work gets
            funded, made, and released.
          </p>
        </FadeIn>

        <FadeIn delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                icon: <Play className="w-6 h-6" />,
                title: 'Kinora',
                tagline: 'Watch & fund it',
                description:
                  'Stream indie films and series, donate your monthly slices, and back crowdfunding campaigns.',
                cta: 'Start watching',
                onClick: () => navigate('/browse'),
              },
              {
                icon: <Clapperboard className="w-6 h-6" />,
                title: 'Kinora Guild',
                tagline: 'Make it',
                description:
                  'The creator network. Build your craft profile, crew up on real productions, and earn verified credits.',
                cta: 'Join the Guild',
                onClick: () => navigate('/guild'),
              },
              {
                icon: <Upload className="w-6 h-6" />,
                title: 'Kinora Studio',
                tagline: 'Publish it',
                description:
                  'Upload your films, run crowdfunding campaigns, and keep 100% of every slice donated to you.',
                cta: 'Start publishing',
                onClick: () => window.open('https://publish.slicetv.app', '_blank', 'noopener,noreferrer'),
              },
            ].map((app) => (
              <Card
                key={app.title}
                className="relative h-full border border-border/40 bg-card/60 backdrop-blur-sm glow-card transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-4">
                    {app.icon}
                  </div>
                  <p className="text-xs font-bold text-primary/60 uppercase tracking-widest">
                    {app.tagline}
                  </p>
                  <h3 className="text-lg font-bold mt-1 mb-2">{app.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {app.description}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6 w-full h-11 group"
                    onClick={app.onClick}
                  >
                    {app.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════
          FAQ
         ════════════════════════════════════════ */}
      <section className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <FadeIn className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            Frequently Asked Questions
          </h2>
        </FadeIn>

        <FadeIn delay={100}>
          <div>
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                open={openFAQ === i}
                toggle={() => setOpenFAQ(openFAQ === i ? null : i)}
              />
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════
          FINAL CTA
         ════════════════════════════════════════ */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.04] to-primary/[0.08] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <FadeIn className="text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight max-w-2xl mx-auto leading-tight">
              Ready to stream with{' '}
              <span className="text-gradient">purpose</span>?
            </h2>
            <p className="text-muted-foreground mt-4 max-w-md mx-auto text-base md:text-lg">
              Join Kinora and start supporting the creators who are building the
              future of independent entertainment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button
                size="lg"
                className="group text-base px-10 h-12"
                onClick={() => navigate('/auth')}
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-10 h-12"
                onClick={() => navigate('/browse')}
              >
                <Play className="w-5 h-5 mr-2" />
                Browse Content
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}

export default Landing
